import React, { useMemo, useState } from "react";
import "./custom.css";
import 'react-toastify/dist/ReactToastify.css'
import {
    Grid,
    Card,
    CardContent,
    Typography,
    CardActions, Avatar,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
    Tooltip,
} from "@material-ui/core/";
import { CommonDialog } from "../common/CommonComponents/Dialog";
import { PrimaryButton } from "../common/CommonComponents/Buttons";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Input from "@material-ui/core/Input";
import InputAdornment from "@material-ui/core/InputAdornment";
import CloudIcon from "@material-ui/icons/Cloud";
import RoomIcon from "@material-ui/icons/Room";
import PersonIcon from "@material-ui/icons/Person";
import AssignmentIcon from "@material-ui/icons/Assignment";
import LinkIcon from "@material-ui/icons/Link";
import BusinessIcon from "@material-ui/icons/Business";

/**
   * @author shilpa.adikeshava
   * @version 1.0
 */

// Same map used by BedrockAccessCards.js — keep both in sync when adding a new status.
const STATUS_STYLES = {
    active: { bg: "#e6f6ec", fg: "#1e7d3c", dot: "#2e9e52" },
    "in-use": { bg: "#e8f0fe", fg: "#1a56c4", dot: "#2f6fed" },
    provisioning: { bg: "#fff4e0", fg: "#a3610a", dot: "#f0a02c" },
    inactive: { bg: "#f1f1f3", fg: "#63656b", dot: "#9a9ca3" },
};

const getStatusStyle = (status) =>
    STATUS_STYLES[(status || "").toLowerCase()] || STATUS_STYLES.inactive;

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="llm-row">
        <Icon className="llm-row-icon" />
        <div>
            <span className="llm-row-label">{label}</span>
            <span className="llm-row-value">{value || "—"}</span>
        </div>
    </div>
);

const AccessCards = (props) => {
    const [open, setOpen] = useState(false);
    const [modalData, setModalData] = useState({});
    const [expiryDate, setExpiryDate] = useState('');
    const [deploymentName, setDeploymentName] = useState('');
    const [values, setValues] = useState({ password: "", showKey: false });

    const data = props.data || [];
    const query = props.searchQuery || '';

    const handleClose = () => setOpen(false);

    const handleOpen = (row) => {
        setModalData(row);
        const date = row.SPExpiryDate || "";
        const [first] = date.split('T');
        setExpiryDate(first || "");
        setDeploymentName(row.DeploymentName || "");
        setOpen(true);
    };

    const handleClickShowKey = () => setValues({ ...values, showKey: !values.showKey });
    const handleMouseDownKey = (event) => event.preventDefault();
    const handleKeyChange = (prop) => (event) => setValues({ ...values, [prop]: event.target.value });

    const filteredData = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return data;
        return data.filter((row) =>
            [
                row.ClusterId,
                row.OpenAIServiceName,
                row.AWSHostedRegion,
                row.Status,
                row.CustomerEnterpriseID,
                row.WBSECode,
                row.ClientName,
            ]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(q))
        );
    }, [data, query]);

    return (
        <div style={{ marginBottom: "5%" }}>
            {data.length > 0 && (
                <div className="llm-toolbar">
                    <span className="llm-result-count">
                        {filteredData.length} of {data.length} deployment{data.length !== 1 ? "s" : ""}
                        {query ? ` matching "${query}"` : ""}
                    </span>
                </div>
            )}

            <Grid container spacing={3} direction="row" alignItems="stretch">
                {filteredData.length === 0 ? (
                    <div className="llm-empty-state">
                        <b style={{ fontSize: "1.05rem" }}>
                            {data.length === 0
                                ? "Looks like you haven’t deployed any models."
                                : `No deployments match "${query}".`}
                        </b>
                    </div>
                ) : (
                    filteredData.map((row, key) => {
                        const statusStyle = getStatusStyle(row.Status);
                        return (
                            <Grid item xs={12} sm={6} md={4} key={row.ClusterId || key}>
                                <Card variant="outlined" className="llm-card">
                                    <div className="llm-card-header">
                                        <Avatar className="llm-avatar">{(row.ClusterId || "?").toString().charAt(0)}</Avatar>
                                        <div className="llm-header-text">
                                            <Typography className="llm-client-name" title={row.ClusterId}>
                                                {row.ClusterId}
                                            </Typography>
                                            <Typography className="llm-profile-name" title={row.OpenAIServiceName}>
                                                ID: {row.OpenAIServiceName}
                                            </Typography>
                                        </div>
                                        <Chip
                                            className="llm-status-chip"
                                            style={{ background: statusStyle.bg, color: statusStyle.fg }}
                                            label={
                                                <>
                                                    {row.Status}
                                                    <span className="llm-status-dot" style={{ background: statusStyle.dot }} />
                                                </>
                                            }
                                        />
                                    </div>

                                    <CardContent className="llm-body">
                                        <InfoRow icon={BusinessIcon} label="Tenant ID" value={row.TenantID} />
                                        <InfoRow icon={AssignmentIcon} label="Subscription ID" value={row.AzSubscriptionID} />
                                        <InfoRow icon={RoomIcon} label="Region" value={row.AWSHostedRegion} />
                                        <InfoRow icon={PersonIcon} label="Customer Enterprise ID" value={row.CustomerEnterpriseID} />
                                        <InfoRow icon={CloudIcon} label="OpenAI Endpoint" value={row.OpenAIEndPoint} />
                                        <InfoRow icon={LinkIcon} label="APIM Base URL" value={row.apim_baseurl} />
                                        <InfoRow icon={LinkIcon} label="APIM Auth URL" value={row.apim_auth_url} />
                                    </CardContent>

                                    <CardActions className="llm-card-actions">
                                        <Tooltip title={row.OpenAIServiceName || ""}>
                                            <span className="llm-id-text">Svc: {row.OpenAIServiceName}</span>
                                        </Tooltip>
                                        <span className="llm-show-more-btn" onClick={() => handleOpen(row)}>
                                            Show more →
                                        </span>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })
                )}
            </Grid>

            <CommonDialog
                className="AzureOpenAIDialog"
                open={open}
                onClose={handleClose}
                title={"Azure OpenAI Access Status"}
                dialogType={"fui"}
                dialogContent={
                    <DialogContent>
                        <DialogContentText id="alert-dialog-descreption">
                            <Typography sx={{ mt: 2, mb: 1 }} component="div">
                                <p style={{ color: "black" }}>Please find the details below:</p>

                                {[
                                    ["OpenAI ServiceName", modalData.OpenAIServiceName],
                                    ["OpenAI EndPoint", modalData.OpenAIEndPoint],
                                    ["Key Vault Name", modalData.KeyVaultName],
                                    ["Key Vault OpenAI Secret Name", modalData.KeyVaultOAISecretName],
                                    ["SP ID", modalData.SPID],
                                ].map(([label, value]) => (
                                    <div className="llm-dialog-row" key={label}>
                                        <span className="llm-dialog-label-wide">{label}:</span>
                                        <span className="llm-dialog-value">{value}</span>
                                    </div>
                                ))}

                                <div className="llm-dialog-row">
                                    <span className="llm-dialog-label-wide">SP SecretKey:</span>
                                    <Input
                                        style={{ padding: "0px !important", width: "60%", color: "black" }}
                                        disableUnderline={true}
                                        type={values.showKey ? "text" : "password"}
                                        onChange={handleKeyChange("password")}
                                        value={modalData.SPSecretKey}
                                        inputProps={{ readOnly: true }}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClickShowKey} onMouseDown={handleMouseDownKey}>
                                                    {values.showKey ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </div>

                                {[
                                    ["APIM Name", modalData.apim_name],
                                    ["APIM Region", modalData.apim_region],
                                    ["APIM RG Name", modalData.apim_rg_name],
                                    ["APIM Base URL", modalData.apim_baseurl],
                                    ["APIM Auth URL", modalData.apim_auth_url],
                                    ["Az Subscription ID", modalData.AzSubscriptionID],
                                    ["Az Subscription Name", modalData.AzSubscriptionName],
                                    ["Resource GroupName", modalData.ResourceGroupName],
                                    ["Tenant ID", modalData.TenantID],
                                    ["Vendor", "Azure OpenAI"],
                                    ["Az Region", modalData.AzRegion],
                                    ["AWS Hosted Region", modalData.AWSHostedRegion],
                                    ["Client Name", modalData.ClientName === "myWizard AiOps" || modalData.ClientName === "mywizard aiops" ? "GenWizard AiOps" : modalData.ClientName],
                                    ["Cluster ID", modalData.ClusterId],
                                    ["Contract ID", modalData.ContractID],
                                    ["Customer Enterprise ID", modalData.CustomerEnterpriseID],
                                    ["Secondary Customer Enterprise ID", modalData.SecondaryCustomerEnterpriseID],
                                    ["Environment", modalData.Environment],
                                    ...(modalData.ApplicationName && modalData.ApplicationName.startsWith("genwizard_")
                                        ? []
                                        : [
                                              ["Application Name", modalData.ApplicationName],
                                              ["Instance IP", modalData.InstanceIP],
                                              ["Instance ID", modalData.InstanceId],
                                          ]),
                                    ["Status", modalData.Status],
                                    ["WBSE Code", modalData.WBSECode],
                                    ["Order Date", modalData.OrderDate],
                                    ["SP Expiry Date", expiryDate],
                                ].map(([label, value]) => (
                                    <div className="llm-dialog-row" key={label}>
                                        <span className="llm-dialog-label-wide">{label}:</span>
                                        <span className="llm-dialog-value">{value}</span>
                                    </div>
                                ))}

                                <div style={{ marginTop: "16px" }}>
                                    <b style={{ fontWeight: 600, color: "#3d2266" }}>
                                        Deployment Details <span style={{ fontSize: "18px" }}>&#128071;</span>
                                    </b>
                                    <table className="llm-model-table">
                                        <thead>
                                            <tr>
                                                <th>Deployment ID</th>
                                                <th>Model Name</th>
                                                <th>Model Version</th>
                                                <th>Model Capacity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deploymentName.split(",").map((rowContent, rowID) => (
                                                <tr key={rowID}>
                                                    {rowContent.split("|").map((cell, i) => (
                                                        <td key={i}>{cell}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <br />
                                <p style={{ color: "black" }}>
                                    Check your email for further details and instructions regarding your OpenAI access, including login
                                    credentials, account setup instructions, and any additional steps you need to take.
                                </p>
                                <p style={{ color: "black" }}>Thank you!</p>
                            </Typography>
                        </DialogContentText>
                    </DialogContent>
                }
                dialogActions={
                    <DialogActions>
                        <PrimaryButton
                            data-cy="dialogOkButton"
                            variant="outlined"
                            onClick={handleClose}
                            label={"Close"}
                            className="llm-dialog-close-btn"
                        />
                    </DialogActions>
                }
            >
            </CommonDialog>
        </div>
    );
};

export default AccessCards;